import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { signOut as signOutAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import "./AdminPanel.css";
import AppImage from "../common/AppImage";
import { clearStoredAccessMode } from "../../utils/accessMode";
import { canManageAdminPanel, revokeAdminAccess } from "../../utils/adminAccess";
import {
  isCloudinaryConfigured,
  uploadImageToCloudinary,
  uploadVideoToCloudinary,
} from "../../utils/cloudinary";
import {
  AWARD_TYPES,
  FINAL_AWARD_ID,
  getAwardMaxNominees,
  getAwardName,
  getNomineeImage,
  getNomineeName,
  isSpecialAward,
  sortAwards,
} from "../../utils/awards";

const createEmptyAwardForm = () => ({
  id: "",
  nombre: "",
  descripcion: "",
  type: AWARD_TYPES.NORMAL,
  maxNominees: "4",
  sortOrder: "",
});

const createEmptyNomineeForm = () => ({
  id: "",
  name: "",
  imageUrl: "",
  mediaUrl: "",
  mediaImageUrl: "",
  videoUrl: "",
});

const getFriendlyErrorMessage = (error, fallbackMessage) => {
  if (error?.code === "permission-denied" || error?.code === "firestore/permission-denied") {
    return "Firebase ha rechazado la accion. Revisa las reglas de Firestore para este usuario.";
  }

  if (error?.message) {
    return `${fallbackMessage} Detalle: ${error.message}`;
  }

  return fallbackMessage;
};

const normalizeOptionalUrl = (value) => value.trim();
const getOptionalFieldPayload = (value, keys, removeMissing = false) =>
  keys.reduce((accumulator, key) => {
    accumulator[key] = value ? value : removeMissing ? deleteField() : "";
    return accumulator;
  }, {});
const NOMINEE_UPLOAD_FIELDS = {
  imageUrl: {
    label: "la imagen principal",
    resourceKind: "image",
    accepts: "image/*",
    firestoreKeys: ["imageURL", "imageUrl"],
  },
  mediaUrl: {
    label: "la imagen de contexto",
    resourceKind: "image",
    accepts: "image/*",
    firestoreKeys: ["mediaUrl", "mediaURL"],
  },
  videoUrl: {
    label: "el video",
    resourceKind: "video",
    accepts: "video/*",
    firestoreKeys: ["videoURL", "videoUrl"],
  },
};

const getAwardGroupKey = (award) =>
  isSpecialAward(award, award?.id) ? AWARD_TYPES.SPECIAL : AWARD_TYPES.NORMAL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [awards, setAwards] = useState([]);
  const [selectedAwardId, setSelectedAwardId] = useState("");
  const [awardForm, setAwardForm] = useState(createEmptyAwardForm());
  const [nominees, setNominees] = useState([]);
  const [nomineeForm, setNomineeForm] = useState(createEmptyNomineeForm());
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(true);
  const [nomineesLoading, setNomineesLoading] = useState(false);
  const [isSavingAward, setIsSavingAward] = useState(false);
  const [isSavingNominee, setIsSavingNominee] = useState(false);
  const [uploadingField, setUploadingField] = useState("");
  const [isDeletingAward, setIsDeletingAward] = useState(false);
  const [isResettingVotes, setIsResettingVotes] = useState(false);
  const [isReorderingAwards, setIsReorderingAwards] = useState(false);
  const [nomineeActionId, setNomineeActionId] = useState("");
  const nomineeImageInputRef = useRef(null);
  const nomineeMediaInputRef = useRef(null);
  const nomineeVideoInputRef = useRef(null);

  const selectedAward = useMemo(
    () => awards.find((award) => award.id === selectedAwardId) || null,
    [awards, selectedAwardId]
  );
  const isSelectedAwardSpecial = selectedAward ? isSpecialAward(selectedAward, selectedAward.id) : false;
  const nomineeLimitReached =
    !!selectedAward &&
    !isSelectedAwardSpecial &&
    !nomineeForm.id &&
    nominees.length >= getAwardMaxNominees(selectedAward, selectedAward.id);
  const canManagePanel = canManageAdminPanel();

  const loadAwards = async (preferredAwardId = "") => {
    const premiosSnapshot = await getDocs(collection(db, "premios"));
    const awardsList = sortAwards(
      premiosSnapshot.docs.map((snapshot) => ({
        id: snapshot.id,
        ...snapshot.data(),
      }))
    );

    setAwards(awardsList);

    const nextAwardId =
      preferredAwardId && awardsList.some((award) => award.id === preferredAwardId)
        ? preferredAwardId
        : awardsList[0]?.id || "";

    setSelectedAwardId(nextAwardId);
  };

  const loadNominees = async (awardId) => {
    if (!awardId) {
      setNominees([]);
      return;
    }

    setNomineesLoading(true);

    try {
      const nominadosSnapshot = await getDocs(collection(db, "premios", awardId, "nominados"));
      const nomineesList =
        nominadosSnapshot.docs.map((snapshot) => ({
          id: snapshot.id,
          ...snapshot.data(),
        })) || [];

      nomineesList.sort((left, right) =>
        getNomineeName(left).localeCompare(getNomineeName(right), "es", { sensitivity: "base" })
      );

      setNominees(nomineesList);
    } finally {
      setNomineesLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        await loadAwards();
      } catch (error) {
        if (!cancelled) {
          console.error("Error al cargar premios:", error);
          setFeedback({
            type: "error",
            text: "No hemos podido cargar el panel de administracion.",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedAward) {
      setAwardForm(createEmptyAwardForm());
      setNominees([]);
      setNomineeForm(createEmptyNomineeForm());
      return;
    }

    setAwardForm({
      id: selectedAward.id,
      nombre: selectedAward.nombre || selectedAward.name || "",
      descripcion: selectedAward.descripcion || selectedAward.description || "",
      type: isSpecialAward(selectedAward, selectedAward.id) ? AWARD_TYPES.SPECIAL : AWARD_TYPES.NORMAL,
      maxNominees: String(getAwardMaxNominees(selectedAward, selectedAward.id)),
      sortOrder: String(selectedAward.sortOrder ?? selectedAward.orden ?? ""),
    });
    setNomineeForm(createEmptyNomineeForm());
    loadNominees(selectedAward.id).catch((error) => {
      console.error("Error al cargar nominados:", error);
      setFeedback({
        type: "error",
        text: "No hemos podido cargar los nominados de este premio.",
      });
    });
  }, [selectedAward]);

  const handleCreateNewAward = () => {
    if (!ensureEditorAccess()) {
      return;
    }

    setSelectedAwardId("");
    setAwardForm(createEmptyAwardForm());
    setNomineeForm(createEmptyNomineeForm());
    setNominees([]);
    setFeedback({ type: "", text: "" });
  };

  const handleAwardFieldChange = (field, value) => {
    setAwardForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "type" && value === AWARD_TYPES.NORMAL ? { maxNominees: "4" } : {}),
    }));
  };

  const handleNomineeFieldChange = (field, value) => {
    setNomineeForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const ensureEditorAccess = () => {
    if (canManagePanel) {
      return true;
    }

    setFeedback({
      type: "error",
      text: "El panel admin requiere modo premium activo y la clave de administrador en esta sesion.",
    });
    return false;
  };

  const syncNomineeFieldInState = (nomineeId, field, value) => {
    setNomineeForm((current) => ({
      ...current,
      [field]: value,
    }));
    setNominees((currentNominees) =>
      currentNominees.map((nominee) =>
        nominee.id === nomineeId
          ? {
              ...nominee,
              [field]: value,
              ...(field === "imageUrl" ? { imageURL: value } : {}),
              ...(field === "mediaUrl" ? { mediaURL: value } : {}),
              ...(field === "videoUrl" ? { videoURL: value } : {}),
            }
          : nominee
      )
    );
  };

  const persistUploadedNomineeField = async (field, value) => {
    if (!selectedAward || !nomineeForm.id) {
      return false;
    }

    const uploadFieldConfig = NOMINEE_UPLOAD_FIELDS[field];

    if (!uploadFieldConfig) {
      return false;
    }

    const nomineeRef = doc(db, "premios", selectedAward.id, "nominados", nomineeForm.id);

    await updateDoc(nomineeRef, {
      ...getOptionalFieldPayload(value, uploadFieldConfig.firestoreKeys, true),
      updatedAt: serverTimestamp(),
    });
    syncNomineeFieldInState(nomineeForm.id, field, value);

    return true;
  };

  const handleSelectNomineeAsset = (field) => async (event) => {
    const file = event.target.files?.[0];
    const uploadFieldConfig = NOMINEE_UPLOAD_FIELDS[field];

    if (!file) {
      return;
    }

    if (!ensureEditorAccess()) {
      event.target.value = "";
      return;
    }

    if (!uploadFieldConfig) {
      event.target.value = "";
      return;
    }

    if (
      (uploadFieldConfig.resourceKind === "image" && !file.type.startsWith("image/")) ||
      (uploadFieldConfig.resourceKind === "video" && !file.type.startsWith("video/"))
    ) {
      setFeedback({
        type: "error",
        text:
          uploadFieldConfig.resourceKind === "video"
            ? "Selecciona un archivo de video valido para este campo."
            : "Selecciona un archivo de imagen valido para este campo.",
      });
      event.target.value = "";
      return;
    }

    setUploadingField(field);
    setFeedback({ type: "", text: "" });

    try {
      const uploadResult =
        uploadFieldConfig.resourceKind === "video"
          ? await uploadVideoToCloudinary(file)
          : await uploadImageToCloudinary(file);
      const uploadedUrl = uploadResult.secureUrl;
      const persistedDirectly = await persistUploadedNomineeField(field, uploadedUrl);

      if (!persistedDirectly) {
        setNomineeForm((current) => ({
          ...current,
          [field]: uploadedUrl,
        }));
      }

      setFeedback({
        type: "success",
        text: persistedDirectly
          ? `Cloudinary ha subido ${uploadFieldConfig.label} y ya se ha guardado en Firebase.`
          : `Cloudinary ha subido ${uploadFieldConfig.label}. Guarda el nominado para persistirla en Firebase.`,
      });
    } catch (error) {
      console.error(`Error al subir ${uploadFieldConfig.label}:`, error);
      setFeedback({
        type: "error",
        text: getFriendlyErrorMessage(error, `No hemos podido subir ${uploadFieldConfig.label}.`),
      });
    } finally {
      setUploadingField("");
      event.target.value = "";
    }
  };

  const syncUsersAfterAwardRename = async (oldAwardName, newAwardName) => {
    if (!oldAwardName || !newAwardName || oldAwardName === newAwardName) {
      return;
    }

    const usersSnapshot = await getDocs(collection(db, "users"));

    for (const userSnapshot of usersSnapshot.docs) {
      const userData = userSnapshot.data();

      if (!userData[oldAwardName]) {
        continue;
      }

      await setDoc(
        userSnapshot.ref,
        {
          [newAwardName]: userData[oldAwardName],
        },
        { merge: true }
      );

      await updateDoc(userSnapshot.ref, {
        [oldAwardName]: deleteField(),
      });
    }
  };

  const clearAwardFromUsers = async (award) => {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const awardName = getAwardName(award);

    for (const userSnapshot of usersSnapshot.docs) {
      const userData = userSnapshot.data();

      if (isSpecialAward(award, award.id)) {
        const updates = {};

        if (award.id === FINAL_AWARD_ID && userData.finalVote) {
          updates.finalVote = deleteField();
        }

        if (userData.specialVotes?.[award.id]) {
          updates[`specialVotes.${award.id}`] = deleteField();
        }

        if (Object.keys(updates).length > 0) {
          await updateDoc(userSnapshot.ref, updates);
        }

        continue;
      }

      if (userData[awardName]) {
        await updateDoc(userSnapshot.ref, {
          [awardName]: deleteField(),
        });
      }
    }
  };

  const clearNomineeFromUsers = async (award, nominee) => {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const awardName = getAwardName(award);
    const nomineeName = getNomineeName(nominee);

    for (const userSnapshot of usersSnapshot.docs) {
      const userData = userSnapshot.data();

      if (isSpecialAward(award, award.id)) {
        const updates = {};

        if (award.id === FINAL_AWARD_ID && userData.finalVote?.[nomineeName] !== undefined) {
          updates[`finalVote.${nomineeName}`] = deleteField();
        }

        if (userData.specialVotes?.[award.id]?.[nomineeName] !== undefined) {
          updates[`specialVotes.${award.id}.${nomineeName}`] = deleteField();
        }

        if (Object.keys(updates).length > 0) {
          await updateDoc(userSnapshot.ref, updates);
        }

        continue;
      }

      if (userData[awardName]?.[nominee.id] !== undefined) {
        await updateDoc(userSnapshot.ref, {
          [`${awardName}.${nominee.id}`]: deleteField(),
        });
      }
    }
  };

  const syncSpecialAwardNomineeRename = async (award, oldName, newName) => {
    if (!oldName || !newName || oldName === newName) {
      return;
    }

    const usersSnapshot = await getDocs(collection(db, "users"));

    for (const userSnapshot of usersSnapshot.docs) {
      const userData = userSnapshot.data();
      const currentFinalVote = userData.finalVote;
      const currentSpecialVote = userData.specialVotes?.[award.id];

      if (award.id === FINAL_AWARD_ID && currentFinalVote?.[oldName] !== undefined) {
        await setDoc(
          userSnapshot.ref,
          {
            finalVote: {
              [newName]: currentFinalVote[oldName],
            },
          },
          { merge: true }
        );

        await updateDoc(userSnapshot.ref, {
          [`finalVote.${oldName}`]: deleteField(),
        });
      }

      if (currentSpecialVote?.[oldName] !== undefined) {
        await setDoc(
          userSnapshot.ref,
          {
            specialVotes: {
              [award.id]: {
                [newName]: currentSpecialVote[oldName],
              },
            },
          },
          { merge: true }
        );

        await updateDoc(userSnapshot.ref, {
          [`specialVotes.${award.id}.${oldName}`]: deleteField(),
        });
      }
    }
  };

  const handleSaveAward = async (event) => {
    event.preventDefault();
    setFeedback({ type: "", text: "" });

    if (!ensureEditorAccess()) {
      return;
    }

    const trimmedName = awardForm.nombre.trim();
    const trimmedDescription = awardForm.descripcion.trim();
    const isEditing = Boolean(awardForm.id);
    const forcedType =
      awardForm.id === FINAL_AWARD_ID ? AWARD_TYPES.SPECIAL : awardForm.type || AWARD_TYPES.NORMAL;
    const maxNominees =
      forcedType === AWARD_TYPES.SPECIAL ? Math.max(Number(awardForm.maxNominees || 6), 3) : 4;

    if (!trimmedName || !trimmedDescription) {
      setFeedback({
        type: "error",
        text: "Nombre y descripcion son obligatorios para guardar un premio.",
      });
      return;
    }

    setIsSavingAward(true);

    try {
      const payload = {
        nombre: trimmedName,
        name: trimmedName,
        descripcion: trimmedDescription,
        description: trimmedDescription,
        tipo: forcedType,
        type: forcedType,
        maxNominees,
        capacidadNominados: maxNominees,
        sortOrder: awardForm.sortOrder ? Number(awardForm.sortOrder) : 0,
        orden: awardForm.sortOrder ? Number(awardForm.sortOrder) : 0,
        updatedAt: serverTimestamp(),
      };

      if (isEditing) {
        const currentAwardSnapshot = await getDoc(doc(db, "premios", awardForm.id));
        const currentAwardData = currentAwardSnapshot.data() || {};
        const previousAwardName = getAwardName(currentAwardData);

        await updateDoc(doc(db, "premios", awardForm.id), payload);
        await syncUsersAfterAwardRename(previousAwardName, trimmedName);
        await loadAwards(awardForm.id);
        setFeedback({
          type: "success",
          text: "Premio actualizado correctamente.",
        });
      } else {
        const newAwardRef = await addDoc(collection(db, "premios"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        await loadAwards(newAwardRef.id);
        setFeedback({
          type: "success",
          text: "Premio creado correctamente.",
        });
      }
    } catch (error) {
      console.error("Error al guardar premio:", error);
      setFeedback({
        type: "error",
        text: getFriendlyErrorMessage(error, "No hemos podido guardar el premio."),
      });
    } finally {
      setIsSavingAward(false);
    }
  };

  const handleDeleteAward = async () => {
    if (!ensureEditorAccess()) {
      return;
    }

    if (!selectedAward) {
      return;
    }

    if (selectedAward.id === FINAL_AWARD_ID) {
      setFeedback({
        type: "error",
        text: "El premio final no se puede eliminar para no romper el flujo principal.",
      });
      return;
    }

    if (!window.confirm(`Vas a eliminar "${getAwardName(selectedAward)}" y todos sus nominados. Continuar?`)) {
      return;
    }

    setIsDeletingAward(true);
    setFeedback({ type: "", text: "" });

    try {
      const awardIdToDelete = selectedAward.id;
      const awardNameToDelete = getAwardName(selectedAward);
      const remainingAwards = awards.filter((award) => award.id !== awardIdToDelete);
      const nextAwardId = remainingAwards[0]?.id || "";
      const nominadosSnapshot = await getDocs(collection(db, "premios", selectedAward.id, "nominados"));
      const batch = writeBatch(db);

      nominadosSnapshot.docs.forEach((snapshot) => {
        batch.delete(snapshot.ref);
      });

      batch.delete(doc(db, "premios", selectedAward.id));
      await batch.commit();

      setAwards(remainingAwards);
      setSelectedAwardId(nextAwardId);
      setNominees([]);
      setNomineeForm(createEmptyNomineeForm());
      setFeedback({
        type: "success",
        text: "Premio eliminado correctamente.",
      });

      try {
        await clearAwardFromUsers(selectedAward);
        await loadAwards(nextAwardId);
      } catch (syncError) {
        console.error("El premio se elimino, pero fallo una sincronizacion posterior:", syncError);
        setFeedback({
          type: "warning",
          text: `El premio "${awardNameToDelete}" se elimino en Firebase, pero fallo una sincronizacion secundaria del panel. Recarga si ves datos desactualizados.`,
        });
      }
    } catch (error) {
      console.error("Error al eliminar premio:", error);
      setFeedback({
        type: "error",
        text: getFriendlyErrorMessage(error, "No hemos podido eliminar el premio."),
      });
    } finally {
      setIsDeletingAward(false);
    }
  };

  const handleResetAwardVotes = async () => {
    if (!ensureEditorAccess()) {
      return;
    }

    if (!selectedAward) {
      return;
    }

    if (!window.confirm(`Se resetearan todos los votos de "${getAwardName(selectedAward)}". Continuar?`)) {
      return;
    }

    setIsResettingVotes(true);
    setFeedback({ type: "", text: "" });

    try {
      const nominadosSnapshot = await getDocs(collection(db, "premios", selectedAward.id, "nominados"));
      const batch = writeBatch(db);

      nominadosSnapshot.docs.forEach((snapshot) => {
        batch.update(snapshot.ref, {
          votes: 0,
          votedUsers: {},
        });
      });

      await batch.commit();
      setNominees((currentNominees) =>
        currentNominees.map((nominee) => ({
          ...nominee,
          votes: 0,
          votedUsers: {},
        }))
      );
      setFeedback({
        type: "success",
        text: "Votos del premio reseteados a cero.",
      });

      try {
        await clearAwardFromUsers(selectedAward);
        await loadNominees(selectedAward.id);
      } catch (syncError) {
        console.error("Los votos se resetearon, pero fallo una sincronizacion posterior:", syncError);
        setFeedback({
          type: "warning",
          text: `Los votos de "${getAwardName(selectedAward)}" se han reseteado en Firebase, pero el panel no ha podido refrescar todos los datos secundarios.`,
        });
      }
    } catch (error) {
      console.error("Error al resetear votos:", error);
      setFeedback({
        type: "error",
        text: getFriendlyErrorMessage(error, "No hemos podido resetear los votos del premio."),
      });
    } finally {
      setIsResettingVotes(false);
    }
  };

  const handleEditNominee = (nominee) => {
    setNomineeForm({
      id: nominee.id,
      name: getNomineeName(nominee),
      imageUrl: nominee.imageURL || nominee.imageUrl || "",
      mediaUrl: nominee.mediaUrl || nominee.mediaURL || "",
      mediaImageUrl: nominee.mediaImageURL || nominee.mediaImageUrl || "",
      videoUrl: nominee.videoURL || nominee.videoUrl || "",
    });
  };

  const handleSaveNominee = async (event) => {
    event.preventDefault();
    setFeedback({ type: "", text: "" });

    if (!ensureEditorAccess()) {
      return;
    }

    if (!selectedAward) {
      setFeedback({
        type: "error",
        text: "Selecciona o crea primero un premio.",
      });
      return;
    }

    const trimmedName = nomineeForm.name.trim();

    if (!trimmedName) {
      setFeedback({
        type: "error",
        text: "El nombre del nominado es obligatorio.",
      });
      return;
    }

    if (nomineeLimitReached) {
      setFeedback({
        type: "error",
        text: "Este premio normal ya ha alcanzado el limite de 4 nominados.",
      });
      return;
    }

    setIsSavingNominee(true);

    try {
      const normalizedImageUrl = normalizeOptionalUrl(nomineeForm.imageUrl);
      const normalizedMediaUrl = normalizeOptionalUrl(nomineeForm.mediaUrl);
      const normalizedMediaImageUrl = normalizeOptionalUrl(nomineeForm.mediaImageUrl);
      const normalizedVideoUrl = normalizeOptionalUrl(nomineeForm.videoUrl);
      const basePayload = {
        nombre: trimmedName,
        name: trimmedName,
        updatedAt: serverTimestamp(),
      };
      const createPayload = {
        ...basePayload,
        ...getOptionalFieldPayload(normalizedImageUrl, ["imageURL", "imageUrl"]),
        ...getOptionalFieldPayload(normalizedMediaUrl, ["mediaUrl", "mediaURL"]),
        ...getOptionalFieldPayload(normalizedMediaImageUrl, ["mediaImageURL", "mediaImageUrl"]),
        ...getOptionalFieldPayload(normalizedVideoUrl, ["videoURL", "videoUrl"]),
      };
      const updatePayload = {
        ...basePayload,
        ...getOptionalFieldPayload(normalizedImageUrl, ["imageURL", "imageUrl"], true),
        ...getOptionalFieldPayload(normalizedMediaUrl, ["mediaUrl", "mediaURL"], true),
        ...getOptionalFieldPayload(normalizedMediaImageUrl, ["mediaImageURL", "mediaImageUrl"], true),
        ...getOptionalFieldPayload(normalizedVideoUrl, ["videoURL", "videoUrl"], true),
      };

      if (nomineeForm.id) {
        const nomineeRef = doc(db, "premios", selectedAward.id, "nominados", nomineeForm.id);
        const nomineeSnapshot = await getDoc(nomineeRef);
        const currentNominee = nomineeSnapshot.data() || {};

        await updateDoc(nomineeRef, updatePayload);
        const savedNominee = {
          id: nomineeForm.id,
          ...currentNominee,
          ...createPayload,
        };

        setNominees((currentNominees) =>
          [...currentNominees]
            .map((currentNomineeItem) =>
              currentNomineeItem.id === nomineeForm.id ? savedNominee : currentNomineeItem
            )
            .sort((left, right) =>
              getNomineeName(left).localeCompare(getNomineeName(right), "es", { sensitivity: "base" })
            )
        );

        setFeedback({
          type: "success",
          text: "Nominado actualizado correctamente.",
        });

        try {
          if (isSpecialAward(selectedAward, selectedAward.id)) {
            await syncSpecialAwardNomineeRename(
              selectedAward,
              getNomineeName(currentNominee),
              trimmedName
            );
          }

          await loadNominees(selectedAward.id);
        } catch (syncError) {
          console.error("El nominado se guardo, pero fallo una sincronizacion posterior:", syncError);
          setFeedback({
            type: "warning",
            text: `El nominado "${trimmedName}" se ha guardado en Firebase, pero el panel no ha podido refrescar todas las referencias relacionadas.`,
          });
        }
      } else {
        const newNomineeRef = await addDoc(collection(db, "premios", selectedAward.id, "nominados"), {
          ...createPayload,
          votes: 0,
          votedUsers: {},
          createdAt: serverTimestamp(),
        });
        const savedNominee = {
          id: newNomineeRef.id,
          ...createPayload,
          votes: 0,
          votedUsers: {},
        };

        setNominees((currentNominees) =>
          [...currentNominees, savedNominee].sort((left, right) =>
            getNomineeName(left).localeCompare(getNomineeName(right), "es", { sensitivity: "base" })
          )
        );
        setFeedback({
          type: "success",
          text: "Nominado creado correctamente.",
        });

        try {
          await loadNominees(selectedAward.id);
        } catch (syncError) {
          console.error("El nominado se creo, pero fallo una sincronizacion posterior:", syncError);
          setFeedback({
            type: "warning",
            text: `El nominado "${trimmedName}" se ha creado en Firebase, pero el panel no ha podido refrescar la lista completa.`,
          });
        }
      }

      setNomineeForm(createEmptyNomineeForm());
    } catch (error) {
      console.error("Error al guardar nominado:", error);
      setFeedback({
        type: "error",
        text: getFriendlyErrorMessage(error, "No hemos podido guardar el nominado."),
      });
    } finally {
      setIsSavingNominee(false);
    }
  };

  const handleDeleteNominee = async (nominee) => {
    if (!ensureEditorAccess()) {
      return;
    }

    if (!selectedAward) {
      return;
    }

    if (!window.confirm(`Vas a eliminar a "${getNomineeName(nominee)}". Continuar?`)) {
      return;
    }

    setNomineeActionId(nominee.id);
    setFeedback({ type: "", text: "" });

    try {
      await deleteDoc(doc(db, "premios", selectedAward.id, "nominados", nominee.id));
      setNominees((currentNominees) => currentNominees.filter((currentNominee) => currentNominee.id !== nominee.id));

      if (nomineeForm.id === nominee.id) {
        setNomineeForm(createEmptyNomineeForm());
      }

      setFeedback({
        type: "success",
        text: "Nominado eliminado correctamente.",
      });

      try {
        await clearNomineeFromUsers(selectedAward, nominee);
        await loadNominees(selectedAward.id);
      } catch (syncError) {
        console.error("El nominado se elimino, pero fallo una sincronizacion posterior:", syncError);
        setFeedback({
          type: "warning",
          text: `El nominado "${getNomineeName(nominee)}" se elimino en Firebase, pero el panel no pudo refrescar todas las referencias relacionadas.`,
        });
      }
    } catch (error) {
      console.error("Error al eliminar nominado:", error);
      setFeedback({
        type: "error",
        text: getFriendlyErrorMessage(error, "No hemos podido eliminar el nominado."),
      });
    } finally {
      setNomineeActionId("");
    }
  };

  const handleAdminLogout = async () => {
    revokeAdminAccess();
    clearStoredAccessMode();
    await signOutAuth(auth);
  };

  const canMoveAward = (awardId, direction) => {
    const currentIndex = awards.findIndex((award) => award.id === awardId);
    const targetIndex = currentIndex + direction;

    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= awards.length) {
      return false;
    }

    return getAwardGroupKey(awards[currentIndex]) === getAwardGroupKey(awards[targetIndex]);
  };

  const handleMoveAward = async (awardId, direction) => {
    if (!ensureEditorAccess()) {
      return;
    }

    if (!canMoveAward(awardId, direction)) {
      return;
    }

    const previousAwards = awards;
    const previousSelectedAwardId = selectedAwardId;
    const currentIndex = awards.findIndex((award) => award.id === awardId);
    const targetIndex = currentIndex + direction;
    const reorderedAwards = [...awards];
    const [movedAward] = reorderedAwards.splice(currentIndex, 1);
    reorderedAwards.splice(targetIndex, 0, movedAward);

    const normalizedAwards = reorderedAwards.map((award, index) => ({
      ...award,
      sortOrder: index + 1,
      orden: index + 1,
    }));

    setIsReorderingAwards(true);
    setFeedback({ type: "", text: "" });
    setAwards(normalizedAwards);
    setSelectedAwardId(awardId);

    try {
      const batch = writeBatch(db);

      normalizedAwards.forEach((award, index) => {
        batch.update(doc(db, "premios", award.id), {
          sortOrder: index + 1,
          orden: index + 1,
          updatedAt: serverTimestamp(),
        });
      });

      await batch.commit();
      setFeedback({
        type: "success",
        text: `El orden de "${getAwardName(movedAward)}" se ha actualizado correctamente.`,
      });
    } catch (error) {
      console.error("Error al reordenar premios:", error);
      setAwards(previousAwards);
      setSelectedAwardId(previousSelectedAwardId);
      setFeedback({
        type: "error",
        text: getFriendlyErrorMessage(error, "No hemos podido actualizar el orden de los premios."),
      });
    } finally {
      setIsReorderingAwards(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-shell">
        <div className="admin-empty-state">
          <p className="admin-kicker">Administracion</p>
          <h1>Cargando panel interno</h1>
          <p>Preparando premios, nominados y acciones de mantenimiento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <section className="admin-header">
        <div>
          <p className="admin-kicker">Panel interno</p>
          <h1>Administracion de premios y nominados</h1>
          <p>
            Gestiona Firestore desde la web: crea premios, edita textos, controla nominados, resetea
            votos y trabaja con URLs externas para imagenes y media.
          </p>
        </div>

        <div className="admin-header-actions">
          <button type="button" className="admin-secondary-button" onClick={() => navigate("/home")}>
            Volver a la home
          </button>
          <button
            type="button"
            className="admin-secondary-button"
            onClick={() => navigate("/password-prompt")}
          >
            Acceso privado
          </button>
          <button type="button" className="admin-primary-button" onClick={handleAdminLogout}>
            Cerrar sesion
          </button>
        </div>
      </section>

      {feedback.text ? (
        <p className={`admin-feedback ${feedback.type}`} aria-live="polite">
          {feedback.text}
        </p>
      ) : null}

      <section className="admin-layout">
        <aside className="admin-sidebar">
          <div className="admin-sidebar-header">
            <div>
              <p className="admin-kicker">Premios</p>
              <h2>Listado actual</h2>
            </div>
            <button
              type="button"
              className="admin-primary-button small"
              onClick={handleCreateNewAward}
              disabled={!canManagePanel}
            >
              Nuevo premio
            </button>
          </div>

          <div className="admin-sidebar-tools">
            <button
              type="button"
              className="admin-secondary-button small"
              onClick={() => selectedAwardId && handleMoveAward(selectedAwardId, -1)}
              disabled={!selectedAwardId || isReorderingAwards || !canMoveAward(selectedAwardId, -1)}
            >
              {isReorderingAwards ? "Actualizando..." : "Subir seleccionado"}
            </button>
            <button
              type="button"
              className="admin-secondary-button small"
              onClick={() => selectedAwardId && handleMoveAward(selectedAwardId, 1)}
              disabled={!selectedAwardId || isReorderingAwards || !canMoveAward(selectedAwardId, 1)}
            >
              {isReorderingAwards ? "Actualizando..." : "Bajar seleccionado"}
            </button>
          </div>

          <div className="admin-awards-list">
            {awards.map((award) => (
              <button
                key={award.id}
                type="button"
                className={`admin-award-item ${selectedAwardId === award.id ? "active" : ""}`}
                onClick={() => setSelectedAwardId(award.id)}
              >
                <strong>{getAwardName(award)}</strong>
                <span>
                  {isSpecialAward(award, award.id) ? "Especial" : "Normal"} · max{" "}
                  {getAwardMaxNominees(award, award.id)}
                </span>
              </button>
            ))}
          </div>
        </aside>

        <div className="admin-main">
          <section className="admin-card">
            <div className="admin-card-header">
              <div>
                <p className="admin-kicker">Editor de premios</p>
                <h2>{awardForm.id ? "Editar premio" : "Crear premio"}</h2>
              </div>

              {selectedAward ? (
                <div className="admin-inline-actions">
                  <button
                    type="button"
                    className="admin-secondary-button small"
                    onClick={handleResetAwardVotes}
                    disabled={!canManagePanel || isResettingVotes || isDeletingAward}
                  >
                    {isResettingVotes ? "Reseteando votos..." : "Resetear votos"}
                  </button>
                  <button
                    type="button"
                    className="admin-danger-button small"
                    onClick={handleDeleteAward}
                    disabled={!canManagePanel || isDeletingAward || isResettingVotes}
                  >
                    {isDeletingAward ? "Eliminando premio..." : "Eliminar premio"}
                  </button>
                </div>
              ) : null}
            </div>

            <form className="admin-form-grid" onSubmit={handleSaveAward}>
              <label>
                <span>Nombre del premio</span>
                <input
                  value={awardForm.nombre}
                  onChange={(event) => handleAwardFieldChange("nombre", event.target.value)}
                  placeholder="Ej. Duo mas letal"
                  required
                />
              </label>

              <label>
                <span>Tipo de premio</span>
                <select
                  value={awardForm.id === FINAL_AWARD_ID ? AWARD_TYPES.SPECIAL : awardForm.type}
                  onChange={(event) => handleAwardFieldChange("type", event.target.value)}
                  disabled={awardForm.id === FINAL_AWARD_ID}
                >
                  <option value={AWARD_TYPES.NORMAL}>Normal</option>
                  <option value={AWARD_TYPES.SPECIAL}>Especial</option>
                </select>
              </label>

              <label>
                <span>Orden en la votacion</span>
                <input
                  type="number"
                  min="0"
                  value={awardForm.sortOrder}
                  onChange={(event) => handleAwardFieldChange("sortOrder", event.target.value)}
                  placeholder="0"
                />
              </label>

              <label>
                <span>Capacidad de nominados</span>
                <input
                  type="number"
                  min="3"
                  max={awardForm.type === AWARD_TYPES.SPECIAL || awardForm.id === FINAL_AWARD_ID ? "99" : "4"}
                  value={
                    awardForm.type === AWARD_TYPES.NORMAL && awardForm.id !== FINAL_AWARD_ID
                      ? "4"
                      : awardForm.maxNominees
                  }
                  onChange={(event) => handleAwardFieldChange("maxNominees", event.target.value)}
                  disabled={awardForm.type === AWARD_TYPES.NORMAL && awardForm.id !== FINAL_AWARD_ID}
                />
              </label>

              <label className="full">
                <span>Descripcion</span>
                <textarea
                  value={awardForm.descripcion}
                  onChange={(event) => handleAwardFieldChange("descripcion", event.target.value)}
                  placeholder="Explica brevemente que se vota en esta categoria"
                  required
                  rows={4}
                />
              </label>

              <div className="admin-form-note full">
                Los premios normales se mantienen en hasta 4 nominados. Los premios especiales pueden
                convivir entre si y manejar mas participantes. El orden de votacion define en que
                posicion aparece cada premio dentro de su flujo publico. Si quieres reajustarlo
                rapido, selecciona un premio y usa los controles de subir o bajar del lateral.
              </div>

              <div className="admin-form-actions full">
                <button className="admin-primary-button" type="submit" disabled={!canManagePanel || isSavingAward}>
                  {isSavingAward
                    ? "Guardando premio..."
                    : awardForm.id
                      ? "Guardar cambios del premio"
                      : "Crear premio"}
                </button>
              </div>
            </form>
          </section>

          <section className="admin-card">
            <div className="admin-card-header">
              <div>
                <p className="admin-kicker">Nominados</p>
                <h2>
                  {selectedAward ? `Gestion de nominados para ${getAwardName(selectedAward)}` : "Selecciona un premio"}
                </h2>
              </div>
              {selectedAward ? (
                <span className="admin-chip">
                  {isSelectedAwardSpecial
                    ? "Premio especial editable"
                    : `${nominees.length}/${getAwardMaxNominees(selectedAward, selectedAward.id)} nominados`}
                </span>
              ) : null}
            </div>

            {selectedAward ? (
              <>
                <form className="admin-form-grid admin-form-grid-nominee" onSubmit={handleSaveNominee}>
                  <label className="admin-form-field full">
                    <span>Nombre</span>
                    <input
                      value={nomineeForm.name}
                      onChange={(event) => handleNomineeFieldChange("name", event.target.value)}
                      placeholder="Nombre del nominado"
                      required
                    />
                  </label>

                  <label>
                    <span>imageUrl · foto principal del nominado</span>
                    <input
                      type="url"
                      value={nomineeForm.imageUrl}
                      onChange={(event) => handleNomineeFieldChange("imageUrl", event.target.value)}
                      placeholder="https://... foto principal"
                    />
                    <input
                      ref={nomineeImageInputRef}
                      className="admin-hidden-file-input"
                      type="file"
                      accept={NOMINEE_UPLOAD_FIELDS.imageUrl.accepts}
                      onChange={handleSelectNomineeAsset("imageUrl")}
                    />
                    <div className="admin-upload-actions">
                      <button
                        className="admin-secondary-button small"
                        type="button"
                        onClick={() => nomineeImageInputRef.current?.click()}
                        disabled={Boolean(uploadingField) || !isCloudinaryConfigured()}
                      >
                        {uploadingField === "imageUrl" ? "Subiendo imagen..." : "Subir desde el dispositivo"}
                      </button>
                      <span className="admin-upload-hint">
                        {isCloudinaryConfigured()
                          ? "La subida directa envia la foto principal a Cloudinary."
                          : "Configura Cloudinary para activar la subida directa."}
                      </span>
                    </div>
                  </label>

                  <label>
                    <span>mediaUrl · imagen de prueba o contexto</span>
                    <input
                      type="url"
                      value={nomineeForm.mediaUrl}
                      onChange={(event) => handleNomineeFieldChange("mediaUrl", event.target.value)}
                      placeholder="https://... prueba, meme o contexto"
                    />
                    <input
                      ref={nomineeMediaInputRef}
                      className="admin-hidden-file-input"
                      type="file"
                      accept={NOMINEE_UPLOAD_FIELDS.mediaUrl.accepts}
                      onChange={handleSelectNomineeAsset("mediaUrl")}
                    />
                    <div className="admin-upload-actions">
                      <button
                        className="admin-secondary-button small"
                        type="button"
                        onClick={() => nomineeMediaInputRef.current?.click()}
                        disabled={Boolean(uploadingField) || !isCloudinaryConfigured()}
                      >
                        {uploadingField === "mediaUrl"
                          ? "Subiendo imagen..."
                          : "Subir imagen de contexto"}
                      </button>
                      <span className="admin-upload-hint">
                        {isCloudinaryConfigured()
                          ? "Puedes subir una captura, prueba o imagen de apoyo a Cloudinary."
                          : "Configura Cloudinary para activar la subida directa."}
                      </span>
                    </div>
                  </label>

                  <label>
                    <span>URL de imagen secundaria</span>
                    <input
                      type="url"
                      value={nomineeForm.mediaImageUrl}
                      onChange={(event) => handleNomineeFieldChange("mediaImageUrl", event.target.value)}
                      placeholder="https://..."
                    />
                  </label>

                  <label>
                    <span>videoUrl · video asociado al nominado</span>
                    <input
                      type="url"
                      value={nomineeForm.videoUrl}
                      onChange={(event) => handleNomineeFieldChange("videoUrl", event.target.value)}
                      placeholder="https://... video asociado"
                    />
                    <input
                      ref={nomineeVideoInputRef}
                      className="admin-hidden-file-input"
                      type="file"
                      accept={NOMINEE_UPLOAD_FIELDS.videoUrl.accepts}
                      onChange={handleSelectNomineeAsset("videoUrl")}
                    />
                    <div className="admin-upload-actions">
                      <button
                        className="admin-secondary-button small"
                        type="button"
                        onClick={() => nomineeVideoInputRef.current?.click()}
                        disabled={Boolean(uploadingField) || !isCloudinaryConfigured()}
                      >
                        {uploadingField === "videoUrl" ? "Subiendo video..." : "Subir video"}
                      </button>
                      <span className="admin-upload-hint">
                        {isCloudinaryConfigured()
                          ? "El video se sube a Cloudinary y se asigna directamente a videoUrl."
                          : "Configura Cloudinary para activar la subida directa."}
                      </span>
                    </div>
                  </label>

                  <div className="admin-form-note full">
                    imageUrl se usa como imagen principal del nominado. mediaUrl se usa como imagen
                    adicional para aportar contexto o pruebas. videoUrl guarda el video asociado.
                    Puedes pegar URLs manuales o subir archivos directamente a Cloudinary sin que los
                    campos se mezclen entre si.
                  </div>

                  <div className="admin-form-actions full">
                    <button
                      className="admin-primary-button"
                      type="submit"
                      disabled={
                        !canManagePanel ||
                        isSavingNominee ||
                        nomineeLimitReached ||
                        Boolean(uploadingField)
                      }
                    >
                      {isSavingNominee
                        ? "Guardando nominado..."
                        : nomineeForm.id
                          ? "Guardar cambios del nominado"
                          : "Anadir nominado"}
                    </button>
                    {nomineeForm.id ? (
                      <button
                        className="admin-secondary-button"
                        type="button"
                        onClick={() => setNomineeForm(createEmptyNomineeForm())}
                      >
                        Cancelar edicion
                      </button>
                    ) : null}
                  </div>
                </form>

                <div className="admin-nominees-grid">
                  {nomineesLoading ? (
                    <p className="admin-empty-copy">Cargando nominados...</p>
                  ) : nominees.length > 0 ? (
                    nominees.map((nominee) => (
                      <article key={nominee.id} className="admin-nominee-card">
                        {getNomineeImage(nominee) ? (
                          <div className="admin-nominee-media">
                            <AppImage
                              src={getNomineeImage(nominee)}
                              alt={getNomineeName(nominee)}
                              className="admin-nominee-image"
                              fallbackClassName="admin-image-placeholder"
                            />
                          </div>
                        ) : (
                          <div className="admin-image-placeholder">Sin imagen</div>
                        )}
                        <div className="admin-nominee-copy">
                          <h3>{getNomineeName(nominee)}</h3>
                          <p>Votos acumulados: {nominee.votes || 0}</p>
                        </div>
                        <div className="admin-inline-actions">
                          <button
                            className="admin-secondary-button small"
                            type="button"
                            disabled={!canManagePanel || nomineeActionId === nominee.id || isSavingNominee}
                            onClick={() => handleEditNominee(nominee)}
                          >
                            Editar
                          </button>
                          <button
                            className="admin-danger-button small"
                            type="button"
                            disabled={!canManagePanel || nomineeActionId === nominee.id || isSavingNominee}
                            onClick={() => handleDeleteNominee(nominee)}
                          >
                            {nomineeActionId === nominee.id ? "Eliminando..." : "Eliminar"}
                          </button>
                        </div>
                      </article>
                    ))
                  ) : (
                    <p className="admin-empty-copy">
                      Este premio aun no tiene nominados. Puedes empezar a crearlos desde el formulario.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p className="admin-empty-copy">
                Crea un premio nuevo o selecciona uno existente para gestionar sus nominados.
              </p>
            )}
          </section>
        </div>
      </section>
    </div>
  );
};

export default AdminPanel;
